import React from "react";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { QueryKey, useMutation, queryCache } from "react-query";
import { AxiosError } from "axios";
import ReactMarkdown from "react-markdown";
import { Link } from "react-router-dom";

import { IconButton } from "fe-shared/src/components";
import {
  EditIcon,
  FlagIcon,
  WarningIcon,
} from "fe-shared/src/components/Icons";
import { ApiError } from "fe-shared/src/types/common";
import useDialog from "fe-shared/src/hooks/useDialog";
import {
  TransactionSection,
  Transaction,
  TransactionType,
  TransactionFlag,
  TransactionFlagLink,
} from "fe-shared-transactions/src/types";

import FlagDialog from "../FlagDialog";
import * as api from "../../routes/TransactionDetails/modules/api";

import { FLAG_COLOR, WARNING_COLOR } from "../../consts";

import styles from "./FlagComments.module.scss";

interface Props {
  flags: TransactionFlag[];
  transactionType: TransactionType;
  sectionCode: TransactionSection;
  transactionQueryKey: QueryKey;
  transactionId: number;
  transactionNumber: string;
  activitiesQueryKey: QueryKey;
  isEditable: boolean;
}

const markdownLinksMapper = (
  markdownString: string,
  markdownLinks: TransactionFlagLink[],
  label: string
) => {
  return markdownString.replace(label, () =>
    markdownLinks
      .map(
        (markdownLink) =>
          `[${markdownLink.transactionNumber}](/${
            markdownLink.category === "Transaction"
              ? "transactions"
              : "transfers"
          }/${markdownLink.transactionNumber})`
      )
      .join(", ")
  );
};

const FlagComments = ({
  flags = [],
  transactionType,
  isEditable,
  sectionCode,
  transactionQueryKey,
  transactionId,
  transactionNumber,
  activitiesQueryKey,
}: Props) => {
  const { t } = useTranslation();

  const manualFlag = flags.find((item) => item.isManual);
  const systemFlags = flags.filter((item) => !item.isManual);
  const isList = systemFlags.length > 1;

  const {
    isDialogActive,
    isDialogRendered,
    openDialog,
    closeDialog,
  } = useDialog();

  const [updateFlag, mutationResult] = useMutation<
    void,
    AxiosError<ApiError>,
    api.ActionFlagParams
  >(api.flagTransaction, {
    onSuccess: (res, params) => {
      closeDialog();
      queryCache.invalidateQueries(activitiesQueryKey);
      queryCache.setQueryData(transactionQueryKey, (prevData?: Transaction) => {
        if (prevData) {
          return {
            ...prevData,
            transactionFlags: prevData.transactionFlags.map((f) =>
              f.isManual && f.sectionCode === params.sectionCode
                ? { ...f, ...params }
                : f
            ),
          };
        }
        return prevData!;
      });
    },
  });

  return (
    <>
      {!!manualFlag && (
        <div
          className={classnames(
            styles.transactionFlagComment,
            styles.transactionFlagCommentManual
          )}
        >
          <FlagIcon fillColor={FLAG_COLOR} />
          <pre>{manualFlag.text}</pre>
          {isEditable && (
            <IconButton
              hasTooltip
              id={`${sectionCode}-edit-flag-comment-button`}
              aria-label={t("transactions.actions.editFlag")}
              className={styles.transactionFlagCommentEdit}
              onClick={openDialog}
            >
              <EditIcon fillColor={null} />
            </IconButton>
          )}
          {isDialogRendered && (
            <FlagDialog
              isEditDialog
              transactionId={transactionId}
              transactionType={transactionType}
              sectionCode={sectionCode}
              isActive={isDialogActive}
              onClose={closeDialog}
              onSubmit={updateFlag}
              mutationResult={mutationResult}
              text={manualFlag.text}
              showToParticipant={manualFlag.isVisibleToParticipant}
              transactionNumber={transactionNumber}
            />
          )}
        </div>
      )}
      {!!systemFlags.length && (
        <div className={styles.transactionFlagComment}>
          <WarningIcon fillColor={WARNING_COLOR} />
          <div>
            {systemFlags.map((item) => (
              <pre
                key={item.text}
                className={isList ? styles.transactionFlagCommentListItem : ""}
              >
                <ReactMarkdown
                  source={markdownLinksMapper(
                    item.text,
                    item.links ?? [],
                    "{Links}"
                  )}
                  renderers={{
                    link: (linkProps) => (
                      <Link to={linkProps.href}>{linkProps.children}</Link>
                    ),
                  }}
                />
              </pre>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default FlagComments;
